import { memo, useState } from "react";
import StatCardRow from "../../shared/StatCardRow";
import RecordModal from "../../shared/RecordModal";
import DashboardTable from "../../shared/DashboardTable";
import Timeline from "../../shared/Timeline";
import Pager from "../../shared/Pager";
import InfoBanner from "../../shared/InfoBanner";
import { usePaginatedList } from "../../hooks/usePaginatedList";
import { useMountFlash } from "../../hooks/useMountFlash";
import { fetchCredentials, fetchLmsAssignments, fetchSafetyMeetings } from "../../services/dashboardApi";
import { withId } from "../../utils/drillHelpers";
import { formatDate } from "../../utils/formatters";
import "../../reportDashboard.scss";

const alertColumns = [
  { field: "risk", headerName: "Risk", width: 110 },
  { field: "credentialName", headerName: "Credential", flex: 1, minWidth: 125 },
  { field: "userName", headerName: "User", flex: 1, minWidth: 110 },
  {
    field: "expirationDate",
    headerName: "Expires",
    width: 110,
    valueFormatter: ({ value }) => formatDate(value),
  },
  // Issuing bodies can be full agency/organization names (e.g. "Occupational
  // Safety and Health Administration"), longer than a typical name column.
  { field: "issuingBody", headerName: "Issuing Body", flex: 1, minWidth: 130 },
];

const RISK_MESSAGES = {
  Expired: "This credential has expired and must be renewed immediately.",
  Critical: "This credential expires within 30 days — schedule renewal now.",
  High: "This credential expires within 90 days — plan renewal.",
  Low: "This credential is current and valid.",
  "No Date": "No expiration date is recorded. Review this credential and assign an expiration date to maintain compliance tracking.",
};

const riskActionText = (c) => {
  switch (c.risk) {
    case "Expired":
      return `1. Contact ${c.userName} immediately. 2. Coordinate renewal with ${c.issuingBody}. 3. Restrict activities requiring this certification until renewed.`;
    case "Critical":
      return `1. Initiate renewal process within 48 hours. 2. Confirm ${c.userName} is enrolled in renewal training. 3. Update credential record upon completion.`;
    case "High":
      return "1. Schedule renewal within the next 30 days. 2. Notify the user and their supervisor. 3. Monitor status weekly.";
    case "No Date":
      return `1. Verify credential details with ${c.issuingBody}. 2. Record the correct expiration date in the system. 3. Set a renewal reminder.`;
    default:
      return "No immediate action required. Continue monitoring.";
  }
};

// Provider is intentionally omitted from the Lessons/Courses tables below —
// SequenceXT's LMS schema has no Provider column for lessons or courses, so
// there is no real data to show; the HTML mock invented a Provider value
// that doesn't exist in the source data.
const assignmentColumns = [
  { field: "userName", headerName: "User", flex: 1, minWidth: 110 },
  {
    field: "status",
    headerName: "Status",
    width: 130,
    renderCell: (params) => (
      <span className={`rdPill is-${params.value === "Completed" ? "kudos" : "warning"}`}>
        {params.value?.toUpperCase()}
      </span>
    ),
  },
  {
    field: "dateAssigned",
    headerName: "Assigned",
    width: 110,
    valueFormatter: ({ value }) => formatDate(value),
  },
  {
    field: "dateCompleted",
    headerName: "Completed",
    width: 110,
    valueFormatter: ({ value }) => formatDate(value),
  },
];

const CredentialsSection = ({ data, apiCtx, refetching }) => {
  const { kpis, credTypes = [], issuingBodies = [] } = data;
  const learningManagement = data.learningManagement || {
    lessons: [],
    courses: [],
    safetyMeetingsTotal: 0,
    lessonCourseAssigned: 0,
    lessonCourseCompleted: 0,
    meetingInstructorsAssigned: 0,
    meetingInstructorsCompleted: 0,
    meetingAttendeesTotal: 0,
  };
  const hasDateRange = Boolean(apiCtx.from || apiCtx.to);

  const alertList = usePaginatedList(
    (params) =>
      fetchCredentials(apiCtx, { ...params, risk: "Expired,Critical,High" }),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );

  // Page-payload-fed tables below have no fetch of their own to report; this
  // gives them a brief loading state when the section mounts (tab switch) and
  // the real one whenever the page payload is refetching.
  const mountFlash = useMountFlash();
  const tableLoading = refetching || mountFlash;

  const maxType = Math.max(...credTypes.map((t) => t.count), 1);

  const typeColumns = [
    { field: "credentialName", headerName: "Credential", flex: 1, minWidth: 125 },
    {
      field: "distribution",
      headerName: "Distribution",
      flex: 1,
      minWidth: 95,
      sortable: false,
      valueGetter: (params) => params.row.count,
      renderCell: (params) => (
        <div className="rdBar rdBar--thin">
          <div
            className="rdBar__fill is-accent"
            style={{ "--rd-fill": `${(params.value / maxType) * 100}%` }}
          />
        </div>
      ),
    },
    {
      field: "count",
      headerName: "Count",
      width: 90,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
  ];

  const bodies = issuingBodies;

  const bodyColumns = [
    { field: "issuingBody", headerName: "Issuing Body", flex: 1, minWidth: 130 },
    {
      field: "count",
      headerName: "Credentials",
      width: 110,
      type: "number",
      align: "left",
      headerAlign: "left",
    },
    {
      field: "topTypes",
      headerName: "Top Credentials",
      flex: 1,
      // Joined list of up to 3 credential names — can run longer than a
      // single-value name column.
      minWidth: 140,
      valueGetter: (params) => params.row.types.slice(0, 3).join(", "),
    },
  ];

  const [detail, setDetail] = useState(null);
  const [lmsDetail, setLmsDetail] = useState(null); // { kind: "lesson"|"course", item, filter }
  const [meetingDetail, setMeetingDetail] = useState(null);

  // The Safety Meetings timeline used to render `learningManagement.safetyMeetings`, a full
  // array shipped inside the GetOrgAll payload — every meeting the org had ever held, scrolled
  // in one unbounded list with no page controls. Now server-paged 25 at a time, the same
  // Timeline+Pager shape IncidentsSection's case timeline already uses. The two stat cards
  // above it read learningManagement's own totals, which the API still computes over the whole
  // meeting set, so they do not shrink to the current page.
  const meetingList = usePaginatedList(
    (params) => fetchSafetyMeetings(apiCtx, params),
    [apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
    { pageSize: 25 },
  );

  const assignmentList = usePaginatedList(
    (params) =>
      lmsDetail
        ? fetchLmsAssignments(apiCtx, {
            itemType: lmsDetail.kind === "course" ? "Course" : "Lesson",
            itemId: lmsDetail.item.id,
            status: lmsDetail.filter,
            ...params,
          })
        : Promise.resolve({ rows: [], totalCount: 0 }),
    [lmsDetail?.kind, lmsDetail?.item?.id, lmsDetail?.filter, apiCtx.orgId, apiCtx.token],
  );

  // Same guard-pattern treatment as assignmentList above: keyed on the
  // selected credential type (only meaningful once detail.kind === "type"),
  // fetchPage resolves an empty page otherwise so the hook stays
  // unconditional. Feeds the "type" modal's chips section below.
  const typeHoldersList = usePaginatedList(
    (params) =>
      detail?.kind === "type"
        ? fetchCredentials(apiCtx, { credentialName: detail.payload.credentialName, ...params })
        : Promise.resolve({ rows: [], totalCount: 0 }),
    [detail?.kind, detail?.payload?.credentialName, apiCtx.orgId, apiCtx.token, apiCtx.from, apiCtx.to],
  );

  const openAlertDetail = (c) => setDetail({ kind: "alert", payload: c });
  const openTypeDetail = (t) => setDetail({ kind: "type", payload: t });
  const openBodyDetail = (b) => setDetail({ kind: "body", payload: b });

  const openLessonDetail = (item, filter) => setLmsDetail({ kind: "lesson", item, filter });
  const openCourseDetail = (item, filter) => setLmsDetail({ kind: "course", item, filter });

  const lessonColumns = [
    { field: "title", headerName: "Lesson", flex: 1, minWidth: 125 },
    { field: "category", headerName: "Category", flex: 1, minWidth: 95, valueFormatter: ({ value }) => value || "—" },
    {
      field: "assignedTo",
      headerName: "Assigned",
      width: 100,
      type: "number",
      renderCell: (params) => (
        <span className="rdValue rdValue--action is-accent" onClick={(e) => { e.stopPropagation(); openLessonDetail(params.row, "All"); }}>
          {params.value}
        </span>
      ),
    },
    {
      field: "completedBy",
      headerName: "Completed",
      width: 110,
      type: "number",
      renderCell: (params) => (
        <span className="rdValue rdValue--action is-kudos" onClick={(e) => { e.stopPropagation(); openLessonDetail(params.row, "Completed"); }}>
          {params.value}
        </span>
      ),
    },
    {
      field: "notCompletedBy",
      headerName: "Not Completed",
      width: 150,
      type: "number",
      renderCell: (params) => (
        <span className="rdValue rdValue--action is-warning" onClick={(e) => { e.stopPropagation(); openLessonDetail(params.row, "Not Completed"); }}>
          {params.value}
        </span>
      ),
    },
  ];

  const courseColumns = lessonColumns.map((c) => ({
    ...c,
    ...(c.field === "title" ? { headerName: "Course" } : {}),
    ...(c.field === "assignedTo" ? { renderCell: (params) => (
      <span className="rdValue rdValue--action is-accent" onClick={(e) => { e.stopPropagation(); openCourseDetail(params.row, "All"); }}>{params.value}</span>
    ) } : {}),
    ...(c.field === "completedBy" ? { renderCell: (params) => (
      <span className="rdValue rdValue--action is-kudos" onClick={(e) => { e.stopPropagation(); openCourseDetail(params.row, "Completed"); }}>{params.value}</span>
    ) } : {}),
    ...(c.field === "notCompletedBy" ? { renderCell: (params) => (
      <span className="rdValue rdValue--action is-warning" onClick={(e) => { e.stopPropagation(); openCourseDetail(params.row, "Not Completed"); }}>{params.value}</span>
    ) } : {}),
  }));

  const { lessons, courses } = learningManagement;
  const safetyMeetingsTotal = learningManagement.safetyMeetingsTotal || 0;
  const lmsAssigned = learningManagement.lessonCourseAssigned;
  const lmsCompleted = learningManagement.lessonCourseCompleted;
  const completionRate = lmsAssigned > 0 ? Math.round((lmsCompleted / lmsAssigned) * 1000) / 10 : 0;

  const instructorPct =
    learningManagement.meetingInstructorsAssigned > 0
      ? Math.round((learningManagement.meetingInstructorsCompleted / learningManagement.meetingInstructorsAssigned) * 1000) / 10
      : 0;
  const avgAttendees = safetyMeetingsTotal > 0 ? Math.round((learningManagement.meetingAttendeesTotal / safetyMeetingsTotal) * 10) / 10 : 0;

  const meetingTimelineItems = meetingList.rows.map((m) => {
    const instructorsAssigned = m.instructorsAssigned || 0;
    const instructorsCompleted = m.instructorsCompleted || 0;
    const attendeeCount = (m.attendees || []).length;
    return {
      id: m.safetyMeetingId + "-" + m.scheduledDate,
      date: formatDate(m.scheduledDate),
      title: m.title,
      subtitles: [
        `Instructor: ${m.instructor || "—"}`,
        `${attendeeCount} attendee${attendeeCount === 1 ? "" : "s"} logged`,
      ],
      trailingPill:
        instructorsAssigned > 1
          ? `${instructorsCompleted}/${instructorsAssigned} instructors completed`
          : null,
      badge: m.category || null,
      tone: "accent",
    };
  });

  return (
    <div>
      {hasDateRange && (
        <InfoBanner>
          Credential status and the Learning Management section below —
          Lessons, Courses, and the Training Completion Rate — reflect
          current, all-time data, not the selected date range. Credentials
          only carry an expiration date, not a &quot;date added,&quot; so
          there&apos;s nothing to filter on; Lessons/Courses completion is
          tracked at the assignment level, not per-record, so it isn&apos;t
          date-filterable either. <strong>Safety Meetings below do respect
          the date filter.</strong>
        </InfoBanner>
      )}

      <StatCardRow
        items={[
          {
            label: "Total Credentials",
            value: kpis.totalCredentials,
            tone: "accent",
          },
          {
            label: "Expired",
            value: kpis.expiredCredentials,
            tone: "danger",
            badge: kpis.expiredCredentials > 0 ? "RENEW NOW" : null,
          },
          {
            label: "Expiring ≤90 Days",
            value: kpis.criticalCredentials + kpis.highCredentials,
            tone: "warning",
            badge: kpis.criticalCredentials + kpis.highCredentials > 0 ? "CRITICAL" : null,
          },
          {
            label: "No Expiry Set",
            value: kpis.noDateCredentials,
            tone: "warning",
            subtitle: "Review recommended",
          },
        ]}
        columns={4}
      />

      <div className="rdSectionLabel">Critical Expiration Alerts</div>
      <div className="rdCard">
        <div className="rdTableWrap">
          <div className="rdCard__title rdCard__title--bar">
            <span className="rdCard__titleBar">Priority Credential Alerts</span>
            {alertList.totalCount > 0 && (
              <span className="rdCard__note">Click for risk assessment</span>
            )}
          </div>
          <DashboardTable
            columns={alertColumns}
            rows={withId(alertList.rows, "credentialId")}
            onRowClick={(params) => openAlertDetail(params.row)}
            serverMode
            rowCount={alertList.totalCount}
            page={alertList.page}
            onPageChange={alertList.setPage}
            loading={alertList.loading}
            pageSize={alertList.pageSize}
          />
        </div>
      </div>

      <div className="rdGrid rdGrid--2">
        <div>
          <div className="rdSectionLabel rdSectionLabel--first">
            Top Credentials
          </div>
          <div className="rdCard rdCard--flush">
            <div className="rdCard__title">
              <span>Top Credentials</span>
              {credTypes.length > 0 && (
                <span className="rdCard__note">click for holders</span>
              )}
            </div>
            <DashboardTable
              columns={typeColumns}
              rows={credTypes}
              getRowId={(r) => r.credentialName}
              onRowClick={(params) => openTypeDetail(params.row)}
              loading={tableLoading}
            />
          </div>
        </div>
        <div>
          <div className="rdSectionLabel rdSectionLabel--first">
            Issuing Body Analysis
          </div>
          <div className="rdCard rdCard--flush">
            <div className="rdCard__title">
              <span>Issuing Body Analysis</span>
              {bodies.length > 0 && (
                <span className="rdCard__note">click for credential types</span>
              )}
            </div>
            <DashboardTable
              columns={bodyColumns}
              rows={bodies}
              getRowId={(r) => r.issuingBody}
              onRowClick={(params) => openBodyDetail(params.row)}
              loading={tableLoading}
            />
          </div>
        </div>
      </div>

      <div className="rdSectionLabel">Learning Management</div>
      <StatCardRow
        items={[
          { label: "Lessons", value: lessons.length, tone: "accent" },
          { label: "Courses", value: courses.length, tone: "accent" },
          { label: "Safety Meetings", value: safetyMeetingsTotal, tone: "action" },
          {
            label: "Training Completion Rate",
            value: `${completionRate}%`,
            tone: "kudos",
            subtitle: `${lmsCompleted} of ${lmsAssigned} across Lessons + Courses`,
          },
        ]}
        columns={4}
      />

      <div className="rdGrid rdGrid--2">
        <div>
          <div className="rdCard rdCard--flush">
            <div className="rdCard__title">
              <span>Lessons</span>
              {lessons.length > 0 && (
                <span className="rdCard__note">
                  click Assigned/Completed/Not Completed for detail
                </span>
              )}
            </div>
            <DashboardTable
              columns={lessonColumns}
              rows={lessons}
              getRowId={(r) => r.id}
              loading={tableLoading}
              noRowHover
            />
          </div>
        </div>
        <div>
          <div className="rdCard rdCard--flush">
            <div className="rdCard__title">
              <span>Courses</span>
              {courses.length > 0 && (
                <span className="rdCard__note">
                  click Assigned/Completed/Not Completed for detail
                </span>
              )}
            </div>
            <DashboardTable
              columns={courseColumns}
              rows={courses}
              getRowId={(r) => r.id}
              loading={tableLoading}
              noRowHover
            />
          </div>
        </div>
      </div>

      <div className="rdSectionLabel">Safety Meetings</div>
      <StatCardRow
        items={[
          {
            label: "Instructor Completion",
            value: `${instructorPct}%`,
            tone: "kudos",
            subtitle: `${learningManagement.meetingInstructorsCompleted} of ${learningManagement.meetingInstructorsAssigned} instructor assignments completed`,
          },
          {
            label: "Total Attendees",
            value: learningManagement.meetingAttendeesTotal,
            tone: "action",
            subtitle: `Average ${avgAttendees} per meeting across ${safetyMeetingsTotal} meetings logged`,
          },
        ]}
        columns={2}
      />
      <div className="rdCard">
        <Timeline
          items={meetingTimelineItems}
          emptyLabel="No safety meetings recorded"
          onItemClick={(item) => setMeetingDetail(meetingList.rows.find((m) => `${m.safetyMeetingId}-${m.scheduledDate}` === item.id))}
          maxHeight={320}
        />
        <Pager
          page={meetingList.page}
          pageSize={meetingList.pageSize}
          rowCount={meetingList.totalCount}
          onPageChange={meetingList.setPage}
          loading={meetingList.loading}
        />
      </div>

      {detail?.kind === "alert" && (
        <RecordModal
          open
          onClose={() => setDetail(null)}
          title={detail.payload.credentialName}
          fields={[
            { label: "Risk Level", value: detail.payload.risk },
            { label: "Expires", value: formatDate(detail.payload.expirationDate) },
            { label: "Credential", value: detail.payload.credentialName },
            { label: "User", value: detail.payload.userName },
            { label: "Issuing Body", value: detail.payload.issuingBody },
            { label: "Type", value: detail.payload.credentialType || "—" },
          ]}
          warning={{
            tone: detail.payload.risk === "Expired" ? "danger" : "warning",
            text: RISK_MESSAGES[detail.payload.risk] || "Review this credential.",
          }}
          sections={[
            { title: "Recommended Action", type: "note", text: riskActionText(detail.payload) },
          ]}
        />
      )}

      {detail?.kind === "type" && (
        <RecordModal
          open
          onClose={() => setDetail(null)}
          title={detail.payload.credentialName}
          fields={[
            { label: "Total Holders", value: detail.payload.count },
            {
              label: "Issuing Bodies",
              value: (detail.payload.issuingBodies || []).join(", ") || "—",
            },
          ]}
          sections={[
            {
              title: "Users Holding This Credential",
              type: "chips",
              items: typeHoldersList.rows.map((c) => c.userName).filter(Boolean),
              emptyLabel: "None recorded",
              rowCount: typeHoldersList.totalCount,
              page: typeHoldersList.page,
              pageSize: typeHoldersList.pageSize,
              onPageChange: typeHoldersList.setPage,
              loading: typeHoldersList.loading,
            },
          ]}
        />
      )}

      {detail?.kind === "body" && (
        <RecordModal
          open
          onClose={() => setDetail(null)}
          title={detail.payload.issuingBody}
          fields={[
            { label: "Total Credentials Issued", value: detail.payload.count },
          ]}
          sections={[
            {
              title: "Credentials Issued by This Body",
              type: "chips",
              items: detail.payload.types,
              emptyLabel: "None recorded",
            },
          ]}
        />
      )}

      {lmsDetail && (
        <RecordModal
          open
          onClose={() => setLmsDetail(null)}
          title={lmsDetail.item.title}
          fields={[
            { label: "Category", value: lmsDetail.item.category || "—" },
            {
              label: lmsDetail.kind === "course" ? "Est. Duration" : "Completion Time",
              value: `${lmsDetail.item.completionTimeMin} min`,
            },
            { label: "Assigned", value: lmsDetail.item.assignedTo },
            { label: "Completed", value: lmsDetail.item.completedBy },
            { label: "Not Completed", value: lmsDetail.item.notCompletedBy },
          ]}
          sections={[
            ...(lmsDetail.kind === "course" && lmsDetail.item.includedLessons?.length > 0
              ? [{
                  title: "Included Lessons",
                  type: "bullets",
                  items: lmsDetail.item.includedLessons,
                }]
              : []),
            {
              title: `${lmsDetail.filter === "All" ? "All" : lmsDetail.filter} Assignment Records (${assignmentList.totalCount})`,
              type: "table",
              columns: assignmentColumns,
              rows: withId(assignmentList.rows, "userName"),
              serverMode: true,
              rowCount: assignmentList.totalCount,
              page: assignmentList.page,
              onPageChange: assignmentList.setPage,
              loading: assignmentList.loading,
              pageSize: assignmentList.pageSize,
              emptyLabel: "No records",
            },
          ]}
        />
      )}

      {meetingDetail && (
        <RecordModal
          open
          onClose={() => setMeetingDetail(null)}
          title={meetingDetail.title}
          fields={[
            { label: "Category", value: meetingDetail.category || "—" },
            { label: "Scheduled Date", value: formatDate(meetingDetail.scheduledDate) },
          ]}
          sections={[
            {
              title: `Instructor Completion (${meetingDetail.instructorsCompleted}/${meetingDetail.instructorsAssigned})`,
              type: "roster",
              rows: meetingDetail.instructorRoster,
            },
            {
              title: `Attendees (${meetingDetail.attendees.length})`,
              type: "chips",
              items: meetingDetail.attendees,
              emptyLabel: "No attendees logged",
            },
          ]}
        />
      )}
    </div>
  );
};

export default memo(CredentialsSection);
